import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

const useIsomorphicLayoutEffect =
  typeof document !== "undefined" ? useLayoutEffect : useEffect;

type ImageWithFallbackProps = {
  src: string;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  /** Shown when the URL is missing decodeable image data or load fails. */
  fallback: ReactNode;
};

/**
 * Image that falls back when loading fails, including cases where `error` fires
 * before React attaches `onError` (SSR/hydration, cached failures).
 * Remount with `key={src}` when `src` changes so error state resets.
 */
export function ImageWithFallback({
  src,
  alt,
  className,
  loading = "lazy",
  fallback,
}: ImageWithFallbackProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [imgError, setImgError] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth === 0) {
      setImgError(true);
    }
  }, [src]);

  if (imgError) {
    return <>{fallback}</>;
  }

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      onError={() => setImgError(true)}
    />
  );
}
